import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { Receipt, Loader2 } from "lucide-react";
import { odemeRaporlari } from "../../lib/api";

const COLORS = ["#D4AF37", "#34D399", "#60A5FA", "#F472B6"];

function fmt(n) {
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n || 0);
}

export default function OdemeRaporlariPage() {
    const [yil, setYil] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        odemeRaporlari({ yil })
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [yil]);

    return (
        <div className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="font-display text-2xl text-white flex items-center gap-2">
                        <Receipt className="h-6 w-6 text-[#D4AF37]" />
                        Ödeme Raporları
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Aylık özet · tür bazlı · müvekkil bazlı
                    </p>
                </div>
                <select
                    value={yil}
                    onChange={(e) => setYil(Number(e.target.value))}
                    className="bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm"
                >
                    {[2024, 2025, 2026, 2027].map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-slate-400 py-12">
                    <Loader2 className="h-5 w-5 animate-spin" /> Yükleniyor…
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                            <div className="text-xs text-emerald-400/80 uppercase tracking-wider">
                                Toplam Gelen
                            </div>
                            <div className="text-2xl font-semibold text-emerald-300">
                                {fmt(data?.toplam_gelen)}
                            </div>
                        </div>
                        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
                            <div className="text-xs text-rose-400/80 uppercase tracking-wider">
                                Toplam Giden / Ön Ödeme
                            </div>
                            <div className="text-2xl font-semibold text-rose-300">
                                {fmt(data?.toplam_giden)}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4 mb-6 h-72">
                        <h3 className="text-sm font-semibold text-slate-300 mb-3">Aylık Özet</h3>
                        <ResponsiveContainer width="100%" height="90%">
                            <BarChart data={data?.aylik || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="ay_label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                                <Tooltip
                                    contentStyle={{
                                        background: "#0B0F19",
                                        border: "1px solid #334155",
                                    }}
                                    formatter={(v) => fmt(v)}
                                />
                                <Bar dataKey="gelen" fill="#34D399" name="Gelen" />
                                <Bar dataKey="giden" fill="#F87171" name="Giden" />
                                <Bar dataKey="masraf" fill="#FBBF24" name="Masraf" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                            <h3 className="text-sm font-semibold text-slate-300 mb-3">
                                Ödeme Türü Dağılımı
                            </h3>
                            <div className="h-48">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={data?.tur_bazli || []}
                                            dataKey="toplam"
                                            nameKey="odeme_turu"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={70}
                                            label={({ odeme_turu, toplam }) =>
                                                `${odeme_turu}: ${fmt(toplam)}`
                                            }
                                        >
                                            {(data?.tur_bazli || []).map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={COLORS[i % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => fmt(v)} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4">
                            <h3 className="text-sm font-semibold text-slate-300 mb-3">
                                Müvekkil Bazlı (Top 30)
                            </h3>
                            <div className="max-h-56 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-slate-500 text-xs">
                                            <th className="pb-2">Müvekkil</th>
                                            <th className="pb-2 text-right">Toplam</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data?.muvekkil_bazli || []).map((r, i) => (
                                            <tr
                                                key={i}
                                                className="border-t border-white/5 text-slate-300"
                                            >
                                                <td className="py-1.5">{r.muvekkil}</td>
                                                <td className="py-1.5 text-right tabular-nums">
                                                    {fmt(r.toplam)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
